<template>
  <v-form
    v-if="diagnostico"
    ref="form"
    v-model="valid"
    @submit.prevent="onSubmit"
  >
    <v-row no-gutters>
      <v-col cols="12">
        <app-title :texto="titulo" />
      </v-col>
    </v-row>

    <!-- Mapeamento -->
    <v-row>
      <v-col cols="12">
        <v-card
          flat
          outlined
        >
          <v-card-title class="subtitle-1 primary--text">
            Mapeamento Associado
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12">
                <v-autocomplete
                  v-model="diagnostico.mapeamento"
                  :items="mapeamentos"
                  item-text="nome"
                  label="Mapeamento"
                  placeholder="Digite para pesquisar..."
                  :rules="[(v) => !!v || 'Selecione o mapeamento']"
                  :disabled="isEdicaoBloqueada"
                  prepend-icon="widgets"
                  auto-select-first
                  :clearable="!isEdicaoBloqueada"
                  chips
                  deletable-chips
                  return-object
                >
                  <template v-slot:item="{ item }">
                    <span>{{ item.nome }}</span>
                    <v-chip
                      v-if="item.mapeamentoImportado"
                      class="ml-2"
                      color="success"
                      outlined
                      x-small
                    >
                      Importado
                    </v-chip>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ item.nome }}</span>
                    <v-chip
                      v-if="item.mapeamentoImportado"
                      class="ml-2"
                      color="success"
                      outlined
                      x-small
                    >
                      Importado
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Nome e ano -->
    <v-row>
      <v-col
        cols="12"
        sm="9"
      >
        <v-text-field
          id="nome"
          v-model.trim="diagnostico.nome"
          color="primary"
          label="Nome"
          prepend-icon="library_books"
          required
          disabled
        />
      </v-col>
      <v-col
        cols="12"
        sm="3"
      >
        <app-year-select
          v-model="diagnostico.ano"
          :desabilitado="true"
        />
      </v-col>
    </v-row>

    <!-- Seleção de datas -->
    <v-row>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          flat
          outlined
        >
          <v-card-title class="subtitle-1 primary--text">
            Diagnóstico Perspectiva Comportamental
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Inicial"
                  tooltip="Data inicial para realizar as avaliações comportamentais"
                  :data-inicial="diagnostico.inicioComportamental"
                  :rules="[...rulesDataInicial, ...rulesMaxDataMapeamento]"
                  :min="minInicioComportamental"
                  :max="maxInicioComportamental"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      isDataDesabilitada(diagnostico.inicioComportamental)
                  "
                  @return="
                    (value) => (diagnostico.inicioComportamental = value)
                  "
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Final"
                  tooltip="Data final para realizar as avaliações comportamentais"
                  :data-inicial="diagnostico.fimComportamental"
                  :rules="[
                    ...rulesDataFinal,
                    ...rulesMaxDataMapeamento,
                    ...rulesFimDiagnostico,
                  ]"
                  :min="minFimComportamental"
                  :max="maxFimComportamental"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      diagnostico.inicioComportamental === '' ||
                      isDataDesabilitada(diagnostico.fimComportamental)
                  "
                  :validate="validarFimComportamental"
                  @return="(value) => (diagnostico.fimComportamental = value)"
                  @validate="(value) => (validarFimComportamental = value)"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          flat
          outlined
        >
          <v-card-title class="subtitle-1 primary--text">
            Diagnóstico Perspectiva Técnica (Equipe)
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Inicial"
                  tooltip="Data inicial para realizar a avaliação técnica da equipe"
                  :data-inicial="diagnostico.inicioResponsabilidadeChefia"
                  :rules="[...rulesDataInicial, ...rulesMaxDataMapeamento]"
                  :min="minInicioResponsabilidadeChefia"
                  :max="maxInicioResponsabilidadeChefia"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      isDataDesabilitada(diagnostico.inicioResponsabilidadeChefia)
                  "
                  @return="
                    (value) =>
                      (diagnostico.inicioResponsabilidadeChefia = value)
                  "
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Final"
                  tooltip="Data final para realizar a avaliação técnica da equipe"
                  :data-inicial="diagnostico.fimResponsabilidadeChefia"
                  :rules="[...rulesDataFinal, ...rulesMaxDataMapeamento]"
                  :min="minFimResponsabilidadeChefia"
                  :max="maxFimResponsabilidadeChefia"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      diagnostico.inicioResponsabilidadeChefia === '' ||
                      isDataDesabilitada(diagnostico.fimResponsabilidadeChefia)
                  "
                  @return="
                    (value) => (diagnostico.fimResponsabilidadeChefia = value)
                  "
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          flat
          outlined
        >
          <v-card-title class="subtitle-1 primary--text">
            Diagnóstico Perspectiva Técnica (Autoavaliação)
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Inicial"
                  tooltip="Data inicial para realizar a autoavaliação técnica"
                  :data-inicial="diagnostico.inicioResponsabilidadeServidor"
                  :rules="[...rulesDataInicial, ...rulesMaxDataMapeamento]"
                  :min="minInicioResponsabilidadeServidor"
                  :max="maxInicioResponsabilidadeServidor"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      diagnostico.inicioResponsabilidadeChefia === '' ||
                      diagnostico.fimResponsabilidadeChefia === '' ||
                      isDataDesabilitada(
                        diagnostico.inicioResponsabilidadeServidor
                      )
                  "
                  @return="
                    (value) =>
                      (diagnostico.inicioResponsabilidadeServidor = value)
                  "
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Final"
                  tooltip="Data final para realizar a autoavaliação técnica"
                  :data-inicial="diagnostico.fimResponsabilidadeServidor"
                  :rules="[
                    ...rulesDataFinal,
                    ...rulesMaxDataMapeamento,
                    ...rulesFimDiagnostico,
                  ]"
                  :min="minFimResponsabilidadeServidor"
                  :max="maxFimResponsabilidadeServidor"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      diagnostico.inicioResponsabilidadeServidor === '' ||
                      isDataDesabilitada(diagnostico.fimResponsabilidadeServidor)
                  "
                  :validate="validarFimResponsabilidade"
                  @return="
                    (value) => (diagnostico.fimResponsabilidadeServidor = value)
                  "
                  @validate="(value) => (validarFimResponsabilidade = value)"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          flat
          outlined
        >
          <v-card-title class="subtitle-1 primary--text">
            Desempenho
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col
                cols="12"
                sm="6"
              >
                <app-date-select
                  label="Data Inicial"
                  tooltip="Data mínima para visualização do desempenho"
                  :data-inicial="diagnostico.inicioDesempenho"
                  :rules="[...rulesDataInicial, ...rulesMaxDataMapeamento]"
                  :min="minInicioDesempenho"
                  :desabilitado="
                    !diagnostico.mapeamento ||
                      diagnostico.fimComportamental === '' ||
                      diagnostico.fimResponsabilidadeServidor === '' ||
                      isDataDesabilitada(diagnostico.inicioDesempenho)
                  "
                  @return="(value) => (diagnostico.inicioDesempenho = value)"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="justify-end">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="!valid"
            color="primary"
            class="mr-2"
            type="submit"
            v-on="on"
          >
            {{ isEdicao ? "Editar" : "Cadastrar" }}
          </v-btn>
        </template>
        <span>Clique no botão para {{ isEdicao ? "editar" : "salvar" }} o
          diagnóstico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="!formUpdated"
            color="primary"
            outlined
            class="mr-3"
            @click="reset"
            v-on="on"
          >
            Cancelar
          </v-btn>
        </template>
        <span>Clique no botão para cancelar
          {{ isEdicao ? "a edição" : "o cadastro" }} do diagnóstico</span>
      </v-tooltip>
    </v-row>
  </v-form>
</template>

<script>
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import AppTitle from '@/components/core/AppTitle';
import AppYearSelect from '@/components/core/AppYearSelect';
import AppDateSelect from '@/components/core/AppDateSelect';
import Data from '@/modules/data';

const diagnosticoRepository = RepositoryFactory.get('diagnostico');

export default {
  name: 'DiagnosticoForm',

  components: { AppTitle, AppYearSelect, AppDateSelect },

  props: {
    diagnostico: {
      type: Object,
      default: () => ({
        id: null,
        nome: '',
        ano: Data.get().year(),
        inicioComportamental: '',
        fimComportamental: '',
        inicioResponsabilidadeChefia: '',
        fimResponsabilidadeChefia: '',
        inicioResponsabilidadeServidor: '',
        fimResponsabilidadeServidor: '',
        inicioDesempenho: '',
        mapeamento: null,
      }),
      required: false,
    },
    isEdicao: {
      type: Boolean,
      default: false,
      required: false,
    },
    resetForm: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    return {
      valid: true,
      rulesDataInicial: [(v) => !!v.valor || 'Selecione a data inicial'],
      rulesDataFinal: [(v) => !!v.valor || 'Selecione a data final'],
      rulesMaxDataMapeamento: [
        (v) => !Data.compare('isValid', { a: this.maxDataMapeamento })
          || Data.compare('isAfter', { a: v.valor, b: this.maxDataMapeamento })
          || `A data não pode ser menor ou igual à data de fim do mapeamento (${Data.format(this.maxDataMapeamento)})`,
      ],
      rulesFimDiagnostico: [
        () => !this.diagnostico.fimComportamental
          || !this.diagnostico.fimResponsabilidadeServidor
          || Data.compare('isSame', {
            a: this.diagnostico.fimComportamental,
            b: this.diagnostico.fimResponsabilidadeServidor,
          })
          || 'O fim da perspectiva comportamental deve ser igual ao fim da perspectiva técnica',
      ],
      defaultDiagnostico: {
        id: null,
        nome: '',
        ano: Data.get().year(),
        inicioComportamental: '',
        fimComportamental: '',
        inicioResponsabilidadeChefia: '',
        fimResponsabilidadeChefia: '',
        inicioResponsabilidadeServidor: '',
        fimResponsabilidadeServidor: '',
        inicioDesempenho: '',
        mapeamento: null,
      },
      validarFimComportamental: false,
      validarFimResponsabilidade: false,
    };
  },

  computed: {
    diagnosticos: {
      get() {
        return this.$store.state.diagnostico.diagnosticosComissao;
      },
      set(diagnosticos) {
        this.$store.dispatch('diagnostico/setDiagnosticos', diagnosticos);
      },
    },
    mapeamentos() {
      const comportamentos = [...this.$store.state.mapeamento.mapeamentosComissao];
      return comportamentos.sort((a, b) => a.nome.localeCompare(b.nome));
    },
    titulo() {
      return this.isEdicao ? 'EDIÇÃO DE DIAGNÓSTICO' : 'CADASTRO DE DIAGNÓSTICO';
    },
    dataAtual() {
      return Data.get('YYYY-MM-DD');
    },
    anoAtual() {
      return Data.get().year();
    },
    maxDataMapeamento() {
      if (this.diagnostico.mapeamento) {
        if (
          Data.compare('isAfter', {
            a: this.diagnostico.mapeamento.fimPerComportComissao,
            b: this.diagnostico.mapeamento.fimPerRespConsol,
          })
        ) {
          return this.diagnostico.mapeamento.fimPerComportComissao;
        }
        return this.diagnostico.mapeamento.fimPerRespConsol;
      }
      return '';
    },
    minInicioComportamental() {
      if (this.diagnostico.mapeamento) {
        if (!Data.compare('isAfter', { b: this.maxDataMapeamento })) {
          return this.somarDias(this.maxDataMapeamento, 1);
        }
      }
      return this.dataAtual;
    },
    maxInicioComportamental() {
      if (this.diagnostico.fimComportamental) {
        return this.diagnostico.fimComportamental;
      }
      return this.subtrairDias(this.diagnostico.inicioDesempenho, 1);
    },
    minFimComportamental() {
      return this.diagnostico.inicioComportamental === '' ? this.dataAtual : this.diagnostico.inicioComportamental;
    },
    maxFimComportamental() {
      return this.diagnostico.inicioDesempenho === '' ? '' : this.subtrairDias(this.diagnostico.inicioDesempenho, 1);
    },
    minInicioResponsabilidadeChefia() {
      if (this.diagnostico.mapeamento) {
        if (!Data.compare('isAfter', { b: this.maxDataMapeamento })) {
          return this.somarDias(this.maxDataMapeamento, 1);
        }
      }
      return this.dataAtual;
    },
    maxInicioResponsabilidadeChefia() {
      if (this.diagnostico.fimResponsabilidadeChefia) {
        return this.diagnostico.fimResponsabilidadeChefia;
      }
      return this.subtrairDias(this.diagnostico.inicioDesempenho, 1);
    },
    minFimResponsabilidadeChefia() {
      return this.diagnostico.inicioResponsabilidadeChefia === ''
        ? this.dataAtual
        : this.diagnostico.inicioResponsabilidadeChefia;
    },
    maxFimResponsabilidadeChefia() {
      return this.subtrairDias(this.diagnostico.inicioResponsabilidadeServidor, 1);
    },
    minInicioResponsabilidadeServidor() {
      return this.somarDias(this.diagnostico.fimResponsabilidadeChefia, 1);
    },
    maxInicioResponsabilidadeServidor() {
      return this.diagnostico.fimResponsabilidadeServidor;
    },
    minFimResponsabilidadeServidor() {
      return this.diagnostico.inicioResponsabilidadeServidor === ''
        ? this.dataAtual
        : this.diagnostico.inicioResponsabilidadeServidor;
    },
    maxFimResponsabilidadeServidor() {
      // A subtração de 2 dias no início do desempenho visa permitir pelo menos 1 dia
      // para a comissão acessar os dados com antecedência
      return this.diagnostico.inicioDesempenho === '' ? '' : this.subtrairDias(this.diagnostico.inicioDesempenho, 2);
    },
    minInicioDesempenho() {
      // A soma de 2 dias no mínimo visa permitir pelo menos 1 dia
      // para a comissão acessar os dados com antecedência
      if (
        Data.compare('isAfter', {
          a: this.diagnostico.fimComportamental,
          b: this.diagnostico.fimResponsabilidadeServidor,
        })
      ) {
        return this.somarDias(this.diagnostico.fimComportamental, 2);
      }
      return this.somarDias(this.diagnostico.fimResponsabilidadeServidor, 2);
    },
    isEdicaoBloqueada() {
      return this.isEdicao && this.diagnostico.situacao && this.diagnostico.situacao.id !== 'AGENDADO';
    },
    formUpdated() {
      return JSON.stringify(this.diagnostico) !== JSON.stringify(this.defaultDiagnostico);
    },
  },

  watch: {
    'diagnostico.mapeamento': function (value) {
      if (value) {
        this.diagnostico.nome = value.nome;
        this.diagnostico.ano = value.ano;
      } else {
        this.reset();
      }
    },
    'diagnostico.fimComportamental': function (value) {
      let validar = false;
      if (value && this.diagnostico.fimResponsabilidadeServidor) {
        validar = true;
      }
      this.validarFimResponsabilidade = validar;
    },
    'diagnostico.fimResponsabilidadeServidor': function (value) {
      let validar = false;
      if (value && this.diagnostico.fimComportamental) {
        validar = true;
      }
      this.validarFimComportamental = validar;
    },
    resetForm() {
      this.reset();
    },
  },

  methods: {
    onSubmit() {
      if (this.isEdicao) {
        diagnosticoRepository.update(this.diagnostico).then((response) => {
          const diagnostico = response.data;
          const index = this.diagnosticos.findIndex((d) => d.id === diagnostico.id);
          if (index !== -1) {
            this.diagnosticos.splice(index, 1, diagnostico);
            this.reset();
            this.$store.dispatch('app/success', {
              response,
              message: 'Diagnóstico editado com sucesso!',
            });
          }
        });
      } else {
        diagnosticoRepository.create(this.diagnostico).then((response) => {
          const diagnostico = response.data;
          this.diagnosticos.push(diagnostico);
          this.reset();
          this.$store.dispatch('app/success', {
            response,
            message: 'Diagnóstico cadastrado com sucesso!',
          });
        });
      }
    },
    reset() {
      if (this.isEdicao || this.resetForm) {
        this.$emit('updating', false);
        this.$emit('resetForm', false);
      }
      this.$refs.form.resetValidation();
      this.$emit('diagnostico', { ...this.defaultDiagnostico });
    },
    somarDias(data, dias) {
      if (data && dias) {
        return Data.create(data).add(dias, 'days').format('YYYY-MM-DD');
      }
    },
    subtrairDias(data, dias) {
      if (data && dias) {
        return Data.create(data).subtract(dias, 'days').format('YYYY-MM-DD');
      }
    },
    isDataDesabilitada(data) {
      if (Data.compare('isValid', { a: data })) {
        return this.isEdicaoBloqueada && Data.compare('isAfter', { b: data });
      }
      return false;
    },
  },
};
</script>
